import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";

import {
    getAllUnderGraduateCourse,
    getAllPostGraduateCourse,
    getAllAdmissionType,
    createUnderGraduateCourse,
    createPostGraduateCourse,
    createAdmissionType,
    deleteUnderGraduateCourse,
    deletePostGraduateCourse,
    deleteAdmissionType,
    getAllExploreCourse,
} from "./service";
import Sidebar from "../../../Sidebar/Sidebar";
import Header from "../../../Header/Header";
import { 
    FaPlus, 
    FaTrash, 
    FaEdit, 
    FaTimes, 
    FaChevronRight,
    FaCheckCircle,
    FaGraduationCap,
    FaBook,
    FaUniversity
} from "react-icons/fa";
import { HiOutlineMenu, HiOutlineDocumentText } from "react-icons/hi";

const Courses = () => {
    const navigate = useNavigate();

    // Sidebar state and toggle
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

    const [activeTab, setActiveTab] = useState("undergraduate");
    const [courses, setCourses] = useState([]);
    const [exploreCourses, setExploreCourses] = useState([]);
    const [selectedExploreId, setSelectedExploreId] = useState("");
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [showModal, setShowModal] = useState(false);
    const [courseName, setCourseName] = useState("");
    const [courseNameError, setCourseNameError] = useState("");

    const modalRef = useRef(null);

    const tabConfig = {
        undergraduate: {
            label: "Undergraduate",
            short: "UG",
            icon: FaGraduationCap,
            lightColor: "bg-gray-100",
            textColor: "text-gray-700"
        },
        postgraduate: {
            label: "Postgraduate",
            short: "PG",
            icon: FaBook,
            lightColor: "bg-gray-100",
            textColor: "text-gray-700"
        },
        admission: {
            label: "Admission",
            short: "Admission",
            icon: FaUniversity,
            lightColor: "bg-gray-100",
            textColor: "text-gray-700"
        }
    };

    /* ========================= FETCH COURSES ========================= */
    const fetchCourses = async () => {
        setLoading(true);
        try {
            let res;

            if (activeTab === "undergraduate")
                res = await getAllUnderGraduateCourse();
            else if (activeTab === "postgraduate")
                res = await getAllPostGraduateCourse();
            else
                res = await getAllAdmissionType();

            setCourses(res?.data?.data || []);
        } catch {
            toast.error("Failed to fetch courses");
        } finally {
            setLoading(false);
        }
    };

    /* ========================= FETCH EXPLORE ========================= */
    const fetchExploreCourses = async () => {
        try {
            const res = await getAllExploreCourse();
            setExploreCourses(res?.data?.data || []);
        } catch {
            toast.error("Failed to fetch explore courses");
        }
    };

    useEffect(() => {
        fetchCourses();
        fetchExploreCourses();
    }, [activeTab]);

    /* ========================= DELETE COURSE ========================= */
    const handleDeleteCourse = async (courseId, courseName) => {
        if (!window.confirm(`Are you sure you want to delete "${courseName}"?`))
            return;

        try {
            if (activeTab === "undergraduate")
                await deleteUnderGraduateCourse(courseId);
            else if (activeTab === "postgraduate")
                await deletePostGraduateCourse(courseId);
            else
                await deleteAdmissionType(courseId);

            toast.success("Course deleted successfully", {
                icon: '🗑️',
                duration: 3000
            });
            fetchCourses();
        } catch {
            toast.error("Failed to delete course");
        }
    };

    /* ========================= VALIDATION ========================= */
    const validateCourseName = () => {
        if (!courseName.trim()) {
            setCourseNameError("Course name is required");
            return false;
        }
        if (courseName.length < 3) {
            setCourseNameError("Course name must be at least 3 characters");
            return false;
        }
        setCourseNameError("");
        return true;
    };

    /* ========================= CREATE COURSE ========================= */
    const handleCreateCourse = async () => {
        if (!selectedExploreId) {
            toast.error("Please select an explore course");
            return;
        }

        if (!validateCourseName()) return;

        setSubmitting(true);

        try {
            let createdCourse;

            if (activeTab === "undergraduate") {
                createdCourse = await createUnderGraduateCourse(
                    selectedExploreId,
                    { courseName }
                );
            } else if (activeTab === "postgraduate") {
                createdCourse = await createPostGraduateCourse(
                    selectedExploreId,
                    { courseName }
                );
            } else {
                createdCourse = await createAdmissionType(
                    selectedExploreId,
                    { admissionName: courseName }
                );
            }

            toast.success(
                <div>
                    <p className="font-bold">Course Created Successfully!</p>
                    <p className="text-sm opacity-90">You can now add menus and descriptions</p>
                </div>,
                {
                    icon: '🎉',
                    duration: 4000
                }
            );

            resetModal();
            fetchCourses();
        } catch (error) {
            console.error(error);
            toast.error("Something went wrong while creating the course");
        } finally {
            setSubmitting(false);
        }
    };

    const resetModal = () => {
        setShowModal(false);
        setCourseName("");
        setCourseNameError("");
        setSelectedExploreId(exploreCourses[0]?._id || "");
    };

    useEffect(() => {
        if (exploreCourses.length > 0 && !selectedExploreId) {
            setSelectedExploreId(exploreCourses[0]._id);
        }
    }, [exploreCourses]);

    // Handle escape key for modal
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape' && showModal) {
                resetModal();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [showModal]);

    const TabIcon = tabConfig[activeTab].icon;

    const SkeletonLoader = () => (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 lg:gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 shadow-md border border-gray-200 animate-pulse">
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                    <div className="flex gap-2">
                        <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                        <div className="h-8 bg-gray-200 rounded w-1/2"></div>
                    </div>
                </div>
            ))}
        </div>
    );

    const EmptyState = () => (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16 bg-white rounded-2xl border border-gray-200"
        >
            <div className="w-20 h-20 mx-auto bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <TabIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No {tabConfig[activeTab].label} Courses
            </h3>
            <p className="text-gray-500 mb-6">
                Get started by creating your first {tabConfig[activeTab].label.toLowerCase()} course
            </p>
            <button
                onClick={() => setShowModal(true)}
                className="px-6 py-3 bg-gray-800 text-white rounded-xl hover:bg-gray-900 transition inline-flex items-center gap-2"
            >
                <FaPlus size={16} />
                Create Course
            </button>
        </motion.div>
    );

    const modalVariants = {
        hidden: { opacity: 0, scale: 0.9 },
        visible: { 
            opacity: 1, 
            scale: 1,
            transition: { type: "spring", damping: 25, stiffness: 300 }
        },
        exit: { 
            opacity: 0, 
            scale: 0.9,
            transition: { duration: 0.2 }
        }
    };

    return (
        <div className="flex min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 w-full">
            <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} activePage="/courses" />
            
            <div className="flex-1 w-0">
                <Header toggleSidebar={toggleSidebar} />
                
                <div className="flex-1 overflow-y-auto p-3 sm:p-4 md:p-6 lg:p-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        {/* Header Section */}
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8 lg:mb-10">
                            <div>
                                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800">
                                    Course Management
                                </h1>
                                <p className="text-sm text-gray-500 mt-1">
                                    {courses.length} {tabConfig[activeTab].label} course{courses.length !== 1 ? 's' : ''}
                                </p>
                            </div>

                            <button
                                onClick={() => setShowModal(true)}
                                className="w-full sm:w-auto bg-gray-800 text-white px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 rounded-xl shadow-lg hover:bg-gray-900 transition text-sm sm:text-base flex items-center justify-center gap-2"
                            >
                                <FaPlus size={16} />
                                Add New Course
                            </button>
                        </div>

                        {/* Tabs */}
                        <div className="overflow-x-auto pb-2 mb-6 md:mb-8">
                            <div className="flex gap-2 sm:gap-3 md:gap-4 lg:gap-6 min-w-max">
                                {Object.entries(tabConfig).map(([key, config]) => {
                                    const Icon = config.icon;
                                    return (
                                        <button
                                            key={key}
                                            onClick={() => setActiveTab(key)}
                                            className={`px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 rounded-xl font-medium transition-all text-xs sm:text-sm md:text-base whitespace-nowrap flex items-center gap-2 ${
                                                activeTab === key
                                                    ? 'bg-gray-800 text-white shadow-lg scale-105'
                                                    : 'bg-white text-gray-600 border border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <Icon size={16} />
                                            {config.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Course Cards */}
                        {loading ? (
                            <SkeletonLoader />
                        ) : courses.length === 0 ? (
                            <EmptyState />
                        ) : (
                            <motion.div
                                initial="hidden"
                                animate="visible"
                                variants={{
                                    hidden: { opacity: 0 },
                                    visible: {
                                        opacity: 1,
                                        transition: { staggerChildren: 0.1 }
                                    }
                                }}
                                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6 lg:gap-8"
                            >
                                {courses.map((course, index) => (
                                    <motion.div
                                        key={course._id}
                                        variants={{
                                            hidden: { opacity: 0, y: 20 },
                                            visible: { opacity: 1, y: 0 }
                                        }}
                                        whileHover={{ y: -4 }}
                                        className="bg-white rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 shadow-md hover:shadow-xl transition-all border border-gray-200 group"
                                    >
                                        <div className="flex items-start justify-between mb-4">
                                            <div className={`w-8 h-8 p-1.5 rounded-lg ${tabConfig[activeTab].lightColor} flex items-center justify-center`}>
                                                <TabIcon className={`w-4 h-4 ${tabConfig[activeTab].textColor}`} />
                                            </div>
                                            <span className="text-xs text-gray-400">
                                                ID: {course._id.slice(-6)}
                                            </span>
                                        </div>

                                        <h3 className="text-base sm:text-lg md:text-xl font-bold text-gray-800 mb-4 break-words">
                                            {course.courseName || course.admissionName}
                                        </h3>

                                        {course.description && (
                                            <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                                                {course.description}
                                            </p>
                                        )}

                                        <div className="flex flex-col xs:flex-row gap-2 justify-between mt-4 pt-4 border-t border-gray-100">
                                            <button
                                                onClick={() =>
                                                    navigate(`/admin/course/${activeTab}/${course._id}`)
                                                }
                                                className="flex-1 bg-gray-700 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm hover:bg-gray-800 transition flex items-center justify-center gap-1"
                                            >
                                                <FaEdit size={12} />
                                                Manage
                                            </button>

                                            <button
                                                onClick={() => handleDeleteCourse(course._id, course.courseName || course.admissionName)}
                                                className="flex-1 bg-red-600 text-white px-3 sm:px-4 py-2 rounded-lg text-xs sm:text-sm hover:bg-red-700 transition flex items-center justify-center gap-1"
                                            >
                                                <FaTrash size={12} />
                                                Delete
                                            </button>
                                        </div>
                                    </motion.div>
                                ))}
                            </motion.div>
                        )}
                    </motion.div>
                </div>
            </div>

            {/* Create Course Modal - Simplified */}
            <AnimatePresence>
                {showModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex justify-center items-center p-3 sm:p-4 z-50"
                        onClick={resetModal}
                    >
                        <motion.div
                            ref={modalRef}
                            variants={modalVariants}
                            initial="hidden"
                            animate="visible"
                            exit="exit"
                            className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 shadow-2xl border border-gray-200"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex justify-between items-center mb-6">
                                <div>
                                    <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800">
                                        Create New Course
                                    </h2>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Add menus and descriptions later
                                    </p>
                                </div>
                                <button
                                    onClick={resetModal}
                                    className="p-2 hover:bg-gray-100 rounded-lg transition"
                                >
                                    <FaTimes className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>

                            {/* Explore Course Selection */}
                            <div className="mb-6">
                                <h3 className="text-base font-semibold mb-3 text-gray-700">
                                    Select Explore Course <span className="text-red-500">*</span>
                                </h3>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-48 overflow-y-auto pr-1">
                                    {exploreCourses.map((item) => (
                                        <button
                                            key={item._id}
                                            type="button"
                                            onClick={() => setSelectedExploreId(item._id)}
                                            className={`p-3 rounded-xl border-2 transition-all text-left ${
                                                selectedExploreId === item._id
                                                    ? 'border-gray-800 bg-gray-50 shadow-md'
                                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                            }`}
                                        >
                                            <div className="flex items-center gap-2">
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                                    selectedExploreId === item._id
                                                        ? 'bg-gray-800 text-white'
                                                        : 'bg-gray-100 text-gray-600'
                                                }`}>
                                                    <FaCheckCircle size={12} />
                                                </div>
                                                <span className="text-sm font-medium text-gray-700 line-clamp-1">
                                                    {item.exploreCourse}
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Course Details */}
                            <div className="mb-6">
                                <h3 className="text-base font-semibold mb-3 text-gray-700">
                                    Course Details
                                </h3>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Course Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Bachelor of Technology"
                                        value={courseName}
                                        onChange={(e) => {
                                            setCourseName(e.target.value);
                                            setCourseNameError("");
                                        }}
                                        onBlur={validateCourseName}
                                        className={`w-full border rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-gray-400 transition ${
                                            courseNameError ? 'border-red-300' : 'border-gray-300'
                                        }`}
                                    />
                                    {courseNameError && (
                                        <p className="text-xs text-red-500 mt-1">{courseNameError}</p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-1">
                                        {courseName.length}/100 characters
                                    </p>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    onClick={resetModal}
                                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleCreateCourse}
                                    disabled={submitting}
                                    className="px-6 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition disabled:opacity-50 flex items-center gap-2"
                                >
                                    {submitting ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <FaCheckCircle size={16} />
                                            Create Course
                                        </>
                                    )}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default Courses;


























// import React, { useEffect, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import toast from "react-hot-toast";

// import {
//     getAllUnderGraduateCourse,
//     getAllPostGraduateCourse,
//     getAllAdmissionType,

//     createUnderGraduateCourse,
//     createPostGraduateCourse,
//     createAdmissionType,

//     deleteUnderGraduateCourse,
//     deletePostGraduateCourse,
//     deleteAdmissionType,

//     createUnderGraduateMenu,
//     createPostGraduateMenu,
//     createAdmissionMenu,

//     addArticleInUnderGraduateMenuDescription,
//     createPostGraduateMenuDescription,
//     createAdmissionMenuDescription,
// } from "./service";

// const Courses = () => {
//     const navigate = useNavigate();

//     const [activeTab, setActiveTab] = useState("undergraduate");
//     const [courses, setCourses] = useState([]);
//     const [showModal, setShowModal] = useState(false);

//     const [courseName, setCourseName] = useState("");
//     const [menus, setMenus] = useState([{ menuName: "", description: "" }]);

//     // 🔥 Replace with your real exploreCourseId
//     const exploreCourseId = "PUT_YOUR_EXPLORE_ID_HERE";

//     /* ========================= FETCH COURSES ========================= */
//     const fetchCourses = async () => {
//         try {
//             let res;

//             if (activeTab === "undergraduate")
//                 res = await getAllUnderGraduateCourse();
//             else if (activeTab === "postgraduate")
//                 res = await getAllPostGraduateCourse();
//             else
//                 res = await getAllAdmissionType();

//             setCourses(res?.data?.data || []);
//         } catch {
//             toast.error("Failed to fetch courses");
//         }
//     };

//     useEffect(() => {
//         fetchCourses();
//     }, [activeTab]);

//     /* ========================= DELETE COURSE ========================= */
//     const handleDeleteCourse = async (courseId) => {
//         if (!window.confirm("Are you sure you want to delete this course?"))
//             return;

//         try {
//             if (activeTab === "undergraduate")
//                 await deleteUnderGraduateCourse(courseId);
//             else if (activeTab === "postgraduate")
//                 await deletePostGraduateCourse(courseId);
//             else
//                 await deleteAdmissionType(courseId);

//             toast.success("Course Deleted");
//             fetchCourses();
//         } catch {
//             toast.error("Delete Failed");
//         }
//     };

//     /* ========================= ADD MENU FIELD ========================= */
//     const addMenuField = () => {
//         setMenus([...menus, { menuName: "", description: "" }]);
//     };

//     const handleMenuChange = (index, field, value) => {
//         const updated = [...menus];
//         updated[index][field] = value;
//         setMenus(updated);
//     };

//     /* ========================= CREATE COURSE ========================= */
//     const handleCreateCourse = async () => {
//         if (!courseName.trim())
//             return toast.error("Course name required");

//         try {
//             let createdCourse;

//             if (activeTab === "undergraduate") {
//                 createdCourse = await createUnderGraduateCourse(
//                     exploreCourseId,
//                     { courseName }
//                 );
//             } else if (activeTab === "postgraduate") {
//                 createdCourse = await createPostGraduateCourse(
//                     exploreCourseId,
//                     { courseName }
//                 );
//             } else {
//                 createdCourse = await createAdmissionType(
//                     exploreCourseId,
//                     { admissionName: courseName }
//                 );
//             }

//             const courseId =
//                 createdCourse?.data?.data?._id ||
//                 createdCourse?.data?._id;

//             for (let menu of menus) {
//                 if (!menu.menuName.trim()) continue;

//                 let createdMenu;

//                 if (activeTab === "undergraduate") {
//                     createdMenu = await createUnderGraduateMenu(courseId, {
//                         menuName: menu.menuName,
//                     });
//                 } else if (activeTab === "postgraduate") {
//                     createdMenu = await createPostGraduateMenu(courseId, {
//                         menuName: menu.menuName,
//                     });
//                 } else {
//                     createdMenu = await createAdmissionMenu(courseId, {
//                         menuName: menu.menuName,
//                     });
//                 }

//                 const menuId =
//                     createdMenu?.data?.data?._id ||
//                     createdMenu?.data?._id;

//                 if (menu.description.trim()) {
//                     const formData = new FormData();
//                     formData.append("description", menu.description);

//                     if (activeTab === "undergraduate") {
//                         await addArticleInUnderGraduateMenuDescription(
//                             menuId,
//                             formData
//                         );
//                     } else if (activeTab === "postgraduate") {
//                         await createPostGraduateMenuDescription(
//                             menuId,
//                             formData
//                         );
//                     } else {
//                         await createAdmissionMenuDescription(
//                             menuId,
//                             formData
//                         );
//                     }
//                 }
//             }

//             toast.success("Course Created Successfully 🎉");

//             setShowModal(false);
//             setCourseName("");
//             setMenus([{ menuName: "", description: "" }]);
//             fetchCourses();
//         } catch (error) {
//             console.error(error);
//             toast.error("Something went wrong");
//         }
//     };

//     return (
//         <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 p-10">

//             {/* HEADER */}
//             <div className="flex justify-between items-center mb-10">
//                 <h1 className="text-3xl font-bold text-gray-800">
//                     Course Management
//                 </h1>

//                 <button
//                     onClick={() => setShowModal(true)}
//                     className="bg-indigo-600 text-white px-6 py-3 rounded-xl shadow-lg hover:bg-indigo-700 transition"
//                 >
//                     + Add New Course
//                 </button>
//             </div>

//             {/* TABS */}
//             <div className="flex gap-6 mb-8">
//                 {["undergraduate", "postgraduate", "admission"].map((tab) => (
//                     <button
//                         key={tab}
//                         onClick={() => setActiveTab(tab)}
//                         className={`px-6 py-2 rounded-full font-medium transition ${activeTab === tab
//                                 ? "bg-indigo-600 text-white shadow-lg"
//                                 : "bg-white text-gray-600 hover:bg-gray-200"
//                             }`}
//                     >
//                         {tab === "undergraduate"
//                             ? "UG"
//                             : tab === "postgraduate"
//                                 ? "PG"
//                                 : "Admission"}
//                     </button>
//                 ))}
//             </div>

//             {/* COURSE CARDS */}
//             <div className="grid md:grid-cols-3 gap-8">
//                 {courses.map((course) => (
//                     <div
//                         key={course._id}
//                         className="bg-white rounded-2xl p-6 shadow-md hover:shadow-2xl transition transform hover:-translate-y-2"
//                     >
//                         <h3 className="text-xl font-bold text-gray-800 mb-6">
//                             {course.courseName || course.admissionName}
//                         </h3>

//                         <div className="flex justify-between">
//                             <button
//                                 onClick={() =>
//                                     navigate(`/admin/course/${activeTab}/${course._id}`)
//                                 }
//                                 className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition"
//                             >
//                                 Manage
//                             </button>

//                             <button
//                                 onClick={() => handleDeleteCourse(course._id)}
//                                 className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition"
//                             >
//                                 Delete
//                             </button>
//                         </div>
//                     </div>
//                 ))}
//             </div>

//             {/* MODAL remains same as before (not repeated to save space) */}
//             {showModal && (
//                 <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
//                     <div className="bg-white w-full max-w-2xl rounded-2xl p-8 shadow-2xl overflow-y-auto max-h-[90vh]">
//                         <h2 className="text-2xl font-bold mb-6">
//                             Create New Course
//                         </h2>

//                         <input
//                             type="text"
//                             placeholder="Course Name"
//                             value={courseName}
//                             onChange={(e) => setCourseName(e.target.value)}
//                             className="w-full border px-4 py-2 rounded-lg mb-6"
//                         />

//                         {menus.map((menu, index) => (
//                             <div key={index} className="mb-6 border p-4 rounded-xl bg-gray-50">
//                                 <input
//                                     type="text"
//                                     placeholder="Menu Name"
//                                     value={menu.menuName}
//                                     onChange={(e) =>
//                                         handleMenuChange(index, "menuName", e.target.value)
//                                     }
//                                     className="w-full border px-3 py-2 rounded mb-3"
//                                 />

//                                 <textarea
//                                     placeholder="Menu Description"
//                                     value={menu.description}
//                                     onChange={(e) =>
//                                         handleMenuChange(index, "description", e.target.value)
//                                     }
//                                     className="w-full border px-3 py-2 rounded"
//                                 />
//                             </div>
//                         ))}

//                         <button
//                             onClick={addMenuField}
//                             className="text-indigo-600 font-medium mb-6"
//                         >
//                             + Add Another Menu
//                         </button>

//                         <div className="flex justify-end gap-4">
//                             <button
//                                 onClick={() => setShowModal(false)}
//                                 className="px-5 py-2 bg-gray-400 text-white rounded-lg"
//                             >
//                                 Cancel
//                             </button>

//                             <button
//                                 onClick={handleCreateCourse}
//                                 className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
//                             >
//                                 Create Course
//                             </button>
//                         </div>
//                     </div>
//                 </div>
//             )}
//         </div>
//     );
// };

// export default Courses;













































// import React, { useState, useEffect } from "react";
// import toast from "react-hot-toast";
// import { Menu, X } from "lucide-react";

// import {
//     // Undergraduate
//     createUnderGraduateCourse,
//     getAllUnderGraduateCourse,
//     updateUnderGraduateCourse,
//     deleteUnderGraduateCourse,

//     // Postgraduate
//     createPostGraduateCourse,
//     getAllPostGraduateCourse,
//     updatePostGraduateCourse,
//     deletePostGraduateCourse,

//     // Admission
//     createAdmissionType,
//     getAllAdmissionType,
//     updateAdmissionType,
//     deleteAdmissionType,

//     // Explore
//     getAllExploreCourse,
// } from "./service";

// const Course = () => {
//     const [activeTab, setActiveTab] = useState("undergraduate");
//     const [data, setData] = useState([]);
//     const [exploreCourses, setExploreCourses] = useState([]);
//     const [selectedExploreId, setSelectedExploreId] = useState("");

//     const [newName, setNewName] = useState("");
//     const [editingId, setEditingId] = useState(null);
//     const [editingName, setEditingName] = useState("");

//     const [mobileOpen, setMobileOpen] = useState(false);

//     /* ================= FETCH ================= */

//     const fetchData = async () => {
//         try {
//             let res;

//             if (activeTab === "undergraduate") {
//                 res = await getAllUnderGraduateCourse();
//             } else if (activeTab === "postgraduate") {
//                 res = await getAllPostGraduateCourse();
//             } else {
//                 res = await getAllAdmissionType();
//             }

//             setData(res.data.data || []);
//         } catch {
//             toast.error("Failed to fetch data");
//         }
//     };

//     const fetchExplore = async () => {
//         try {
//             const res = await getAllExploreCourse();
//             setExploreCourses(res.data.data || []);
//             console.log(exploreCourses, res.data);
//         } catch {
//             toast.error("Failed to fetch explore courses");
//         }
//     };

//     useEffect(() => {
//         fetchData();
//         fetchExplore();
//     }, [activeTab]);

//     /* ================= CREATE ================= */

//     const handleCreate = async () => {
//         if (!newName) return toast.error("Field required");
//         if (!selectedExploreId) return toast.error("Select Explore Course");

//         try {
//             if (activeTab === "undergraduate") {
//                 await createUnderGraduateCourse(selectedExploreId, {
//                     courseName: newName,
//                 });
//             } else if (activeTab === "postgraduate") {
//                 await createPostGraduateCourse(selectedExploreId, {
//                     courseName: newName,
//                 });
//             } else {
//                 await createAdmissionType(selectedExploreId, {
//                     admissionName: newName,
//                 });
//             }

//             toast.success("Created Successfully");
//             setNewName("");
//             fetchData();
//         } catch {
//             toast.error("Creation Failed");
//         }
//     };

//     /* ================= UPDATE ================= */

//     const handleUpdate = async (id) => {
//         try {
//             if (activeTab === "undergraduate") {
//                 await updateUnderGraduateCourse(id, { courseName: editingName });
//             } else if (activeTab === "postgraduate") {
//                 await updatePostGraduateCourse(id, { courseName: editingName });
//             } else {
//                 await updateAdmissionType(id, { admissionName: editingName });
//             }

//             toast.success("Updated Successfully");
//             setEditingId(null);
//             fetchData();
//         } catch {
//             toast.error("Update Failed");
//         }
//     };

//     /* ================= DELETE ================= */

//     const handleDelete = async (id) => {
//         try {
//             if (activeTab === "undergraduate") {
//                 await deleteUnderGraduateCourse(id);
//             } else if (activeTab === "postgraduate") {
//                 await deletePostGraduateCourse(id);
//             } else {
//                 await deleteAdmissionType(id);
//             }

//             toast.success("Deleted Successfully");
//             fetchData();
//         } catch {
//             toast.error("Delete Failed");
//         }
//     };

//     /* ================= UI ================= */

//     return (
//         <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
//             {/* NAVBAR */}
//             <div className="bg-blue-900 text-white px-6 py-4 flex justify-between items-center shadow-md">
//                 <h1 className="text-xl font-bold">Admin Course Management</h1>

//                 <div className="hidden md:flex gap-6">
//                     <button onClick={() => setActiveTab("undergraduate")}>
//                         Undergraduate
//                     </button>
//                     <button onClick={() => setActiveTab("postgraduate")}>
//                         Postgraduate
//                     </button>
//                     <button onClick={() => setActiveTab("admission")}>
//                         Admission
//                     </button>
//                 </div>

//                 {/* Mobile Icon */}
//                 <div className="md:hidden">
//                     {mobileOpen ? (
//                         <X onClick={() => setMobileOpen(false)} />
//                     ) : (
//                         <Menu onClick={() => setMobileOpen(true)} />
//                     )}
//                 </div>
//             </div>

//             {/* Mobile Dropdown */}
//             {mobileOpen && (
//                 <div className="bg-blue-800 text-white p-4 md:hidden space-y-2 ">
//                     <div className="hover:cursor-pointer" onClick={() => setActiveTab("undergraduate")} >Undergraduate</div>
//                     <div className="hover:cursor-pointer"  onClick={() => setActiveTab("postgraduate")}>Postgraduate</div>
//                     <div className="hover:cursor-pointer" onClick={() => setActiveTab("admission")}>Admission</div>
//                 </div>
//             )}

//             {/* CONTENT */}
//             <div className="p-8">
//                 <h2 className="text-2xl font-bold text-blue-900 mb-6 capitalize">
//                     {activeTab} Management
//                 </h2>

//                 {/* CREATE SECTION */}
//                 <div className="bg-white p-6 rounded-xl shadow-md mb-8 flex flex-col md:flex-row gap-4">
//                     <select
//                         value={selectedExploreId}
//                         onChange={(e) => setSelectedExploreId(e.target.value)}
//                         className="border px-4 py-2 rounded-lg"
//                     >
//                         <option value="">Select Explore Course</option>
//                         {exploreCourses.map((item) => (
//                             <option key={item._id} value={item._id}>
//                                 {item.courseName}
//                             </option>
//                         ))}
//                     </select>

//                     <input
//                         type="text"
//                         placeholder="Enter Name"
//                         value={newName}
//                         onChange={(e) => setNewName(e.target.value)}
//                         className="flex-1 border px-4 py-2 rounded-lg"
//                     />

//                     <button
//                         onClick={handleCreate}
//                         className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
//                     >
//                         Create
//                     </button>
//                 </div>

//                 {/* LIST */}
//                 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
//                     {data.map((item) => (
//                         <div
//                             key={item._id}
//                             className="bg-white p-6 rounded-xl shadow hover:shadow-lg transition"
//                         >
//                             {editingId === item._id ? (
//                                 <>
//                                     <input
//                                         value={editingName}
//                                         onChange={(e) => setEditingName(e.target.value)}
//                                         className="w-full border px-3 py-2 rounded mb-3"
//                                     />
//                                     <div className="flex gap-2">
//                                         <button
//                                             onClick={() => handleUpdate(item._id)}
//                                             className="bg-green-600 text-white px-4 py-2 rounded"
//                                         >
//                                             Save
//                                         </button>
//                                         <button
//                                             onClick={() => setEditingId(null)}
//                                             className="bg-gray-400 text-white px-4 py-2 rounded"
//                                         >
//                                             Cancel
//                                         </button>
//                                     </div>
//                                 </>
//                             ) : (
//                                 <>
//                                     <h3 className="text-lg font-semibold text-blue-800 mb-4">
//                                         {item.courseName ||
//                                             item.admissionName ||
//                                             item.name}
//                                     </h3>

//                                     <div className="flex justify-between">
//                                         <button
//                                             onClick={() => {
//                                                 setEditingId(item._id);
//                                                 setEditingName(
//                                                     item.courseName ||
//                                                     item.admissionName ||
//                                                     ""
//                                                 );
//                                             }}
//                                             className="bg-yellow-500 text-white px-4 py-2 rounded"
//                                         >
//                                             Edit
//                                         </button>

//                                         <button
//                                             onClick={() => handleDelete(item._id)}
//                                             className="bg-red-600 text-white px-4 py-2 rounded"
//                                         >
//                                             Delete
//                                         </button>
//                                     </div>
//                                 </>
//                             )}
//                         </div>
//                     ))}
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default Course;
